import { createFileRoute, Link } from "@tanstack/react-router";

import { HomeNav } from "@/components/home-nav";
import { SiteFooter } from "@/components/site-footer";
import { getJourney, journeyProjects } from "@/data/journeys";
import { releases } from "@/data/releases";

import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/sound-works")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://www.luizajesusdoprado.com/sound-works" },
      { title: "Sound — Music & Experiments | Luiza Jesus do Prado" },
      {
        name: "description",
        content:
          "Sound installations, noise, music projects and sonic experiments by Luiza Jesus do Prado.",
      },
      { property: "og:title", content: "Sound — Music & Experiments | Luiza Jesus do Prado" },
      { property: "og:description", content: "Sound installations, noise and music projects." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Sound,
});

function Sound() {
  const { lang } = useI18n();
  const journey = getJourney("sound");
  const items = journeyProjects("sound");
  const top = items.slice(0, 2);
  const bottom = items.slice(2);
  const hero = items[0]?.cover;

  return (
    <div className="min-h-screen bg-bone text-ink">
      <HomeNav />

      <main id="main-content">
        {/* RED BROADCAST PANEL */}
        <section className="relative overflow-hidden bg-red">
          {hero ? (
            <img
              src={hero}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full object-cover opacity-45 contrast-125"
            />
          ) : null}
          <div className="absolute inset-0 bg-red/45" />

          <div className="relative flex min-h-[86svh] flex-col justify-between px-6 pb-10 pt-28 md:px-12 md:pt-36">
            <div className="flex items-start justify-between font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-bone">
              <span>Studio session</span>
              <span>Transmission — {new Date().getFullYear()}</span>
            </div>

            <div className="mt-16 space-y-3">
              <div className="font-mono text-[10px] uppercase leading-[1.5] tracking-[0.18em] text-bone/85">
                Timecode:
                <br />
                {journey.description[lang]}
                <br />
                Session 04
              </div>

              {top.map((p, i) => (
                <Link
                  key={p.slug}
                  to="/work/$slug"
                  params={{ slug: p.slug }}
                  className={`group block ${i % 2 === 1 ? "text-right" : ""}`}
                >
                  <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-bone/80">
                    {p.year}
                  </span>
                  <span className="block font-sans text-[clamp(1.8rem,6vw,4.4rem)] font-medium uppercase leading-[0.92] tracking-[-0.02em] text-bone transition-opacity duration-300 group-hover:opacity-70">
                    {p.title}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* BONE PANEL */}
        <section className="bg-bone px-6 pb-24 pt-8 md:px-12">
          <div className="flex items-start justify-between font-mono text-[10px] font-medium uppercase leading-[1.5] tracking-[0.2em] text-red">
            <span>
              Sound + noise
              <br />
              São Paulo — BR
            </span>
            <span className="text-right">
              Art direction:
              <br />
              Luiza Jesus do Prado
            </span>
          </div>

          <div className="mt-20 space-y-14">
            {bottom.map((p, i) => (
              <Link
                key={p.slug}
                to="/work/$slug"
                params={{ slug: p.slug }}
                className={`group block ${i % 2 === 1 ? "text-right" : ""}`}
              >
                <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-red/70">
                  {p.year}
                </span>
                <span className="block font-sans text-[clamp(1.8rem,6vw,4.4rem)] font-medium uppercase leading-[0.92] tracking-[-0.02em] text-red transition-opacity duration-300 group-hover:opacity-60">
                  {p.title}
                </span>
              </Link>
            ))}
          </div>

          {/* RELEASES */}
          <section className="mt-24 border-t border-ink/15 pt-8">
            <div className="flex flex-wrap items-baseline justify-between gap-4">
              <h2 className="font-sans text-[clamp(1.4rem,4vw,2.6rem)] font-medium uppercase leading-none tracking-[-0.01em] text-red">
                Releases
              </h2>
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-ink/50">
                Spotify / Bandcamp — {String(releases.length).padStart(2, "0")}
              </span>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-4">
              {releases.map((r) => (
                <a
                  key={r.title}
                  href={r.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group block"
                >
                  <div className="overflow-hidden">
                    <img
                      src={r.cover}
                      alt={r.title}
                      loading="lazy"
                      className="aspect-square w-full object-cover transition-[filter,transform] duration-700 group-hover:scale-[1.03]"
                    />
                  </div>
                  <h3 className="mt-3 font-sans text-[13px] font-medium uppercase leading-tight tracking-[0.02em] text-ink">
                    {r.title}
                  </h3>
                  <span className="mt-1 block font-mono text-[9px] uppercase tracking-[0.26em] text-red/70">
                    {r.platform}
                  </span>
                </a>
              ))}
            </div>
          </section>

          <div className="mt-24 flex items-end justify-between">
            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink/50">
              {String(items.length).padStart(2, "0")} sessions
            </span>
            <span className="text-right font-sans text-[clamp(1rem,2.4vw,1.8rem)] font-medium uppercase leading-[0.95] text-red">
              Listening
              <br />
              Room
            </span>
          </div>

        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
